/**
 * CalendarioList Component - Hedita Design System
 * Feature: Calendario - Visualizzazione calendario prenotazioni
 */

import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, GripVertical, Calendar, Building2 } from "lucide-react";
import { useCalendario, useStruttureCalendario } from "../hooks";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const STATO_BG: Record<string, string> = {
  "Attesa di conferma": "#f97316",
  "Confermata": "#3b82f6",
  "Check-in online effettuato": "#86efac",
  "In corso": "#15803d",
  "Check-out effettuato": "#a855f7",
  "Completata": "#9ca3af",
  "Cancellata": "#ef4444",
  "No show": "#000000",
};

export function CalendarioList() {
  const [mese, setMese] = useState(new Date().getMonth());
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [strutturaSelezionata, setStrutturaSelezionata] = useState<string>("");
  
  const { data: eventi, isLoading: loadingEventi } = useCalendario({ mese, anno, strutturaId: strutturaSelezionata });
  const { data: strutture, isLoading: loadingStrutture } = useStruttureCalendario();

  const giorniNelMese = new Date(anno, mese + 1, 0).getDate();
  const primoGiornoMese = new Date(anno, mese, 1).getDay();
  const giorniPrecedenti = primoGiornoMese === 0 ? 6 : primoGiornoMese - 1;

  const cambiaMese = (delta: number) => {
    if (delta === 1) {
      if (mese === 11) {
        setMese(0);
        setAnno(anno + 1);
      } else {
        setMese(mese + 1);
      }
    } else {
      if (mese === 0) {
        setMese(11);
        setAnno(anno - 1);
      } else {
        setMese(mese - 1);
      }
    }
  };

  const getEventiPerGiorno = (giorno: number) => {
    if (!eventi) return [];
    return eventi.filter(evento => {
      const dataEvento = new Date(evento.data_checkin);
      return dataEvento.getDate() === giorno && 
             dataEvento.getMonth() === mese && 
             dataEvento.getFullYear() === anno;
    });
  };

  if (loadingStrutture) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-muted rounded-xl" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Calendario
          </h1>
          <p className="text-muted-foreground">Visualizza le prenotazioni e disponibilità</p>
        </div>
        
        {/* Selettore struttura */}
        <div className="flex items-center gap-3">
          <select
            value={strutturaSelezionata}
            onChange={(e) => setStrutturaSelezionata(e.target.value)}
            className="px-4 py-2 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">Tutte le strutture</option>
            {strutture?.map(struttura => (
              <option key={struttura.id} value={struttura.id}>
                {struttura.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Navigazione mese */}
      <div className="bg-card rounded-xl border border-border p-4 mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => cambiaMese(-1)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-foreground">
              {MESI[mese]} {anno}
            </h2>
          </div>
          
          <button
            onClick={() => cambiaMese(1)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Calendario */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Giorni della settimana */}
        <div className="grid grid-cols-7 border-b border-border bg-muted/20">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map(giorno => (
            <div key={giorno} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {giorno}
            </div>
          ))}
        </div>

        {/* Giorni del mese */}
        <div className="grid grid-cols-7">
          {/* Giorni precedenti vuoti */}
          {Array.from({ length: giorniPrecedenti }).map((_, i) => (
            <div key={`prev-${i}`} className="p-3 border-r border-b border-border bg-muted/10" />
          ))}

          {/* Giorni del mese */}
          {Array.from({ length: giorniNelMese }).map((_, i) => {
            const giorno = i + 1;
            const eventiGiorno = getEventiPerGiorno(giorno);
            const èOggi = new Date().getDate() === giorno && 
                         new Date().getMonth() === mese && 
                         new Date().getFullYear() === anno;

            return (
              <div
                key={giorno}
                className={`p-3 border-r border-b border-border min-h-[100px] ${
                  èOggi ? 'bg-primary/5' : 'hover:bg-muted/20'
                } transition-colors`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${
                    èOggi ? 'text-primary' : 'text-foreground'
                  }`}>
                    {giorno}
                  </span>
                  {eventiGiorno.length > 0 && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full">
                      {eventiGiorno.length}
                    </span>
                  )}
                </div>
                
                <div className="space-y-1">
                  {eventiGiorno.slice(0, 3).map((evento, idx) => (
                    <div
                      key={idx}
                      className="text-xs p-1 rounded truncate flex items-center gap-1"
                      style={{ 
                        backgroundColor: `${STATO_BG[evento.stato]}20`,
                        color: STATO_BG[evento.stato] 
                      }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: STATO_BG[evento.stato] }}
                      />
                      {evento.ospite_nome}
                    </div>
                  ))}
                  {eventiGiorno.length > 3 && (
                    <div className="text-xs text-muted-foreground">
                      +{eventiGiorno.length - 3} altri
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legenda stati */}
      <div className="mt-6 flex flex-wrap gap-4">
        {Object.entries(STATO_BG).map(([stato, colore]) => (
          <div key={stato} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: colore }}
            />
            <span className="text-sm text-muted-foreground">{stato}</span>
          </div>
        ))}
      </div>

      {/* Loading overlay */}
      {loadingEventi && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
}
